import type { ProviderStatsResponse, SellerActivityStatsResponse, StatisticsSummary } from "./statisticsApi";
import {
  CALCULATION_FORMS,
  GROUP_FORMS,
  formatDays,
  formatDuration,
  formatEur,
  formatPercent,
  formatPolishCount,
} from "./statisticsFormat";

export type KpiColorGroup =
  "orders" | "deposited" | "cancelled" | "margin" | "calculations" | "ordered" | "activity" | "meetings";

export interface KpiItem {
  readonly label: string;
  readonly primary: string;
  readonly secondary?: string;
  readonly sub?: string;
}

export interface KpiSection {
  readonly title: string;
  readonly tooltip?: string;
  readonly colorGroup: KpiColorGroup;
  readonly items: KpiItem[];
}

export function summarySections(summary: StatisticsSummary): KpiSection[] {
  return [
    {
      title: "Zamówienia aktywne",
      tooltip: 'Liczba i wartość zamówień w wybranym okresie, których status nie jest „Anulowane" ani „Do anulowania".',
      colorGroup: "orders",
      items: [countAndValue("Zamówienia", summary.notCancelled.count, summary.notCancelled.valueEur)],
    },
    {
      title: "Grupy zaliczkowane",
      tooltip:
        "Zamówienia aktywne, dla których wpłacono zaliczkę. Procent liczony jest względem liczby zamówień aktywnych.",
      colorGroup: "deposited",
      items: [
        {
          label: "Zaliczki",
          primary: formatPercent(summary.advancePaid.percentOfNotCancelled),
          secondary: `${summary.advancePaid.count} · ${formatEur(summary.advancePaid.valueEur)}`,
        },
      ],
    },
    {
      title: "Grupy anulowane",
      tooltip:
        'Liczba zamówień o statusie „Anulowane" lub „Do anulowania". Procent liczony jest względem wszystkich zamówień w okresie (aktywnych i anulowanych łącznie).',
      colorGroup: "cancelled",
      items: [
        {
          label: "Anulowane",
          primary: formatPercent(summary.cancelled.percentOfTotal),
          secondary: formatPolishCount(summary.cancelled.count, GROUP_FORMS),
        },
      ],
    },
    ...marginSections(summary),
    {
      title: "Kalkulacje",
      tooltip:
        "Liczba i wartość wszystkich kalkulacji utworzonych w wybranym okresie oraz ile z nich utworzyła bezpośrednio osoba kontaktowa klienta.",
      colorGroup: "calculations",
      items: [
        countAndValue("Łącznie", summary.calculations.count, summary.calculations.valueEur),
        {
          label: "Przez osobę kontaktową",
          primary: formatPercent(summary.calculations.byContactPersonPercent),
          secondary: formatPolishCount(summary.calculations.byContactPersonCount, CALCULATION_FORMS),
        },
      ],
    },
    {
      title: "Zamówione łącznie",
      tooltip:
        "Liczba i wartość kalkulacji, które zakończyły się złożeniem zamówienia. Procent liczony jest względem wszystkich kalkulacji w okresie.",
      colorGroup: "ordered",
      items: [
        {
          label: "Łącznie",
          primary: formatPercent(summary.ordered.percentOfCalculations),
          secondary: `${summary.ordered.count} · ${formatEur(summary.ordered.valueEur)}`,
        },
      ],
    },
  ];
}

export function providerSections(stats: ProviderStatsResponse): KpiSection[] {
  return [
    {
      title: "Zamówienia aktywne",
      tooltip:
        'Liczba i wartość zamówień w wybranym okresie zawierających produkt tego dostawcy, których status nie jest „Anulowane" ani „Do anulowania".',
      colorGroup: "orders",
      items: [countAndValue("Zamówienia", stats.notCancelled.count, stats.notCancelled.valueEur)],
    },
    {
      title: "Grupy zaliczkowane",
      tooltip: "Zamówienia aktywne dostawcy, dla których wpłacono zaliczkę.",
      colorGroup: "deposited",
      items: [countAndValue("Zaliczki", stats.advancePaid.count, stats.advancePaid.valueEur)],
    },
    {
      title: "Grupy anulowane",
      tooltip:
        'Liczba i wartość anulowanych zamówień tego dostawcy (procent względem wszystkich jego zamówień). „Śr. okres" to średnia liczba dni między anulowaniem a datą wyjazdu. „Realny" procent liczy tylko anulacje zgłoszone później niż 10 dni po rezerwacji i mniej niż 100 dni przed wyjazdem.',
      colorGroup: "cancelled",
      items: [
        {
          label: "Anulowane",
          primary: formatPercent(stats.cancelled.percentOfTotal),
          secondary: `${stats.cancelled.count} · ${formatEur(stats.cancelled.valueEur)}`,
          sub: `Śr. okres: ${formatDays(stats.cancelled.averageCancellationLeadDays)} · realny ${formatPercent(stats.cancelled.realCancellationPercent)}`,
        },
      ],
    },
  ];
}

export function activitySections(activity: SellerActivityStatsResponse): KpiSection[] {
  const orderedCalculationsCount = Math.round(
    (activity.calculations.count * activity.calculations.orderedPercent) / 100,
  );
  return [
    {
      title: "Spotkania",
      tooltip:
        "Liczba notatek typu „spotkanie online” i „spotkanie w biurze” dodanych przez wybranych sprzedawców w wybranym okresie.",
      colorGroup: "meetings",
      items: [
        { label: "Online", primary: String(activity.meetings.online) },
        { label: "W biurze", primary: String(activity.meetings.office) },
      ],
    },
    {
      title: "Rozmowy telefoniczne",
      tooltip:
        "Liczba i łączny czas trwania rozmów telefonicznych zarejestrowanych dla wybranych sprzedawców w wybranym okresie.",
      colorGroup: "activity",
      items: [
        {
          label: "Połączenia",
          primary: String(activity.phoneCalls.count),
          secondary: formatDuration(activity.phoneCalls.totalDurationSeconds),
        },
      ],
    },
    {
      title: "Kalkulacje",
      tooltip:
        "Kalkulacje utworzone w okresie dla klientów przypisanych do wybranych sprzedawców. „Przez kontakt” — procent utworzonych bezpośrednio przez osobę kontaktową klienta. „Zamówione” — procent, które zakończyły się zamówieniem.",
      colorGroup: "calculations",
      items: [
        {
          label: "Wszystkie",
          primary: String(activity.calculations.count),
          secondary: `${formatPercent(activity.calculations.byContactPersonPercent)} przez kontakt`,
        },
        {
          label: "Zamówione",
          primary: formatPercent(activity.calculations.orderedPercent),
          secondary: `${orderedCalculationsCount} z ${activity.calculations.count} kalkulacji`,
        },
      ],
    },
    {
      title: "Nowe zamówienia",
      tooltip:
        "Liczba zamówień utworzonych w wybranym okresie (wg daty utworzenia, nie daty wyjazdu) dla klientów przypisanych do wybranych sprzedawców.",
      colorGroup: "orders",
      items: [{ label: "Dodane w okresie", primary: String(activity.newOrdersCount), sub: "wg daty utworzenia" }],
    },
  ];
}

function marginSections(summary: StatisticsSummary): KpiSection[] {
  const settled: KpiSection[] = summary.marginSettled
    ? [
        {
          title: "Marża (grupy rozliczone)",
          tooltip:
            "Średnia marża procentowa i średnia kwota marży (EUR) liczone tylko dla zamówień, których wszystkie koszty dostawcy zostały już opłacone. Liczba grup rozliczonych pokazuje, ile takich zamówień wzięto pod uwagę.",
          colorGroup: "margin",
          items: [
            {
              label: "Średnia marża",
              primary: formatPercent(summary.marginSettled.averagePercent),
              secondary: formatEur(summary.marginSettled.amountEur),
            },
            { label: "Grupy rozliczone", primary: String(summary.marginSettled.settledGroupsCount) },
          ],
        },
      ]
    : [];
  const aggregate: KpiSection[] = summary.marginAggregate
    ? [
        {
          title: "Marża sumaryczna",
          tooltip:
            "Łączna marża (suma przychodu minus suma kosztów) dla tych samych rozliczonych zamówień, wyrażona jako procent sumy przychodu (a nie jako średnia z procentów pojedynczych zamówień).",
          colorGroup: "margin",
          items: [
            {
              label: "Marża sumaryczna",
              primary: formatPercent(summary.marginAggregate.percent),
              secondary: formatEur(summary.marginAggregate.amountEur),
            },
          ],
        },
      ]
    : [];
  return [...settled, ...aggregate];
}

function countAndValue(label: string, count: number, valueEur: number | null): KpiItem {
  return { label, primary: String(count), secondary: formatEur(valueEur) };
}
